import { keepPreviousData, useQuery } from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import type {
  IHostDetailsResponse,
  IHostListingQuery,
  IHostListingResponse,
} from "../types";

const hostKeys = {
  all: ["host"] as const,
  details: (id: number) => [...hostKeys.all, "details", id] as const,
  listings: (id: number, query: IHostListingQuery) =>
    [...hostKeys.all, "listings", id, query] as const,
};

/** Raw calls (Laravel). Throw an ApiError on failure. */
export const hostApi = {
  async getHostDetails(id: number): Promise<IHostDetailsResponse> {
    const { data } = await api.laravel.get<IHostDetailsResponse>(`/host/${id}`);
    return data;
  },
  async getHostListing(
    id: number,
    query: IHostListingQuery,
  ): Promise<IHostListingResponse> {
    const { data } = await api.laravel.get<IHostListingResponse>(
      `/listing/host/${id}?${querystring.stringify(query)}`,
    );
    return data;
  },
};

const isValidId = (id: number) => Number.isInteger(id) && id > 0;

export const useHostDetailsQuery = (id: number) =>
  useQuery({
    queryKey: hostKeys.details(id),
    queryFn: () => hostApi.getHostDetails(id),
    enabled: isValidId(id),
    staleTime: 5 * 60 * 1000,
  });

export const useHostListingsQuery = (id: number, query: IHostListingQuery) =>
  useQuery({
    queryKey: hostKeys.listings(id, query),
    queryFn: () => hostApi.getHostListing(id, query),
    enabled: isValidId(id),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
